import React from 'react';

import { snackbar } from '@occc-app/design-system';
import { ConfirmDialog, LinearProgress, Typography } from '@occc/ui-design-system';
import { useGetLocationGroups } from '@occc-app/dataAccessLayer/locationGroup/queries';
import { LocationGroupFragment, useLocationGroupDeleteMutation } from '@occc/graphql';
import { usePaginationParams } from '@occc-app/hooks/usePaginationParams';
import { useIsUserPermittedTo, Feature } from '@occc/utils-access-control';

import { LocationGroupsTable } from './LocationGroupsTable';
import { LocationGroupUpdateDialog } from './LocationGroupUpdateDialog';
import { AFTER_PARAM_NAME, BEFORE_PARAM_NAME } from './constants';

export const LocationGroupsTableContainer: React.FC = () => {
  const [groupToEdit, setGroupToEdit] = React.useState<null | LocationGroupFragment>(null);
  const [groupToDelete, setGroupToDelete] = React.useState<null | LocationGroupFragment>(null);
  const isUserPermittedToEditLocationGroup = useIsUserPermittedTo(Feature.LOCATION_GROUP_EDIT);
  const isUserPermittedToRemoveLocationGroup = useIsUserPermittedTo(Feature.LOCATION_GROUP_DELETE);

  const [paginationInfo, onPageChange] = usePaginationParams({
    beforeParamName: BEFORE_PARAM_NAME,
    afterParamName: AFTER_PARAM_NAME,
  });

  const { loading, error, data, paginationProps } = useGetLocationGroups({
    paginationInfo,
    onPageChange,
  });

  const handleCloseUpdateDialog = React.useCallback(() => {
    setGroupToEdit(null);
  }, []);

  const handleCloseDeleteConfirmationDialog = React.useCallback(() => {
    setGroupToDelete(null);
  }, []);

  const onDeleteCompleted = React.useCallback(() => {
    snackbar.success('Locations Group was deleted');
    handleCloseDeleteConfirmationDialog();
  }, [handleCloseDeleteConfirmationDialog]);

  const onDeleteError = React.useCallback(() => {
    snackbar.error('Unable to delete Location Group. Please try again later.');
  }, []);

  const [deleteLocationGroup, { loading: deleteLoading }] = useLocationGroupDeleteMutation({
    onCompleted: onDeleteCompleted,
    onError: onDeleteError,
    refetchQueries: ['locationGroups'],
  });

  const onDeleteLocationGroup = async () => {
    if (!groupToDelete) {
      return;
    }

    deleteLocationGroup({
      variables: {
        uuid: groupToDelete.uuid,
      },
    });
  };

  return (
    <>
      <LocationGroupsTable
        locationGroups={data}
        loading={loading}
        error={error}
        paginationProps={paginationProps}
        onEdit={setGroupToEdit}
        onDelete={setGroupToDelete}
        isUserPermittedToEditLocationGroup={isUserPermittedToEditLocationGroup}
        isUserPermittedToRemoveLocationGroup={isUserPermittedToRemoveLocationGroup}
      />

      {groupToEdit && (
        <LocationGroupUpdateDialog
          locationGroup={groupToEdit}
          open={true}
          onClose={handleCloseUpdateDialog}
        />
      )}

      {groupToDelete && (
        <ConfirmDialog
          onClose={handleCloseDeleteConfirmationDialog}
          open={true}
          maxWidth='xs'
          confirmButtonText='Delete group'
          confirmButtonProps={{
            onClick: onDeleteLocationGroup,
            disabled: deleteLoading,
            color: 'error',
          }}
        >
          {deleteLoading && <LinearProgress />}
          <Typography>
            Are you sure you want to delete this Location Group? This will unlink all the sites
            associated to this group.
          </Typography>
        </ConfirmDialog>
      )}
    </>
  );
};
